import { useEffect, useMemo, useState } from "react";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type ChannelPrefs,
} from "../api/notifications";

const EVENT_ORDER: Array<{ key: string; label: string }> = [
  { key: "task_completed", label: "Task completed" },
  { key: "task_failed", label: "Task failed" },
  { key: "device_offline", label: "Device offline" },
  { key: "firmware_uploaded", label: "Firmware uploaded" },
  { key: "user_invited", label: "User invited" },
  { key: "tier_limit_approaching", label: "Tier limit approaching" },
];

const DEFAULT_CHANNEL: ChannelPrefs = { inApp: true, email: false };

export function NotificationPreferencesPage() {
  const { data, isLoading, error } = useNotificationPreferences();
  const save = useUpdateNotificationPreferences();
  const [local, setLocal] = useState<Record<string, ChannelPrefs> | null>(null);

  const serverEvents = useMemo(() => data?.data.events ?? null, [data]);

  useEffect(() => {
    if (serverEvents && !local) {
      setLocal(serverEvents);
    }
  }, [serverEvents, local]);

  const events = local ?? serverEvents ?? {};

  function toggle(key: string, channel: keyof ChannelPrefs) {
    const current = events[key] ?? DEFAULT_CHANNEL;
    setLocal({
      ...events,
      [key]: { ...current, [channel]: !current[channel] },
    });
  }

  function handleSave() {
    if (!local) return;
    save.mutate(local);
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Notification Preferences</h2>

      {error && (
        <p className="mb-3 max-w-lg rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          Failed to load preferences: {String((error as Error).message ?? error)}
        </p>
      )}

      <div className="max-w-lg rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="border-b border-gray-200 px-5 py-3 dark:border-riot-navy-700">
          <div className="grid grid-cols-3 text-xs font-medium text-gray-500 dark:text-riot-navy-300">
            <span>Event</span>
            <span className="text-center">In-App</span>
            <span className="text-center">Email</span>
          </div>
        </div>
        {isLoading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading…</div>
        ) : (
          EVENT_ORDER.map(({ key, label }) => {
            const pref = events[key] ?? DEFAULT_CHANNEL;
            return (
              <div
                key={key}
                className="grid grid-cols-3 items-center border-b border-gray-100 px-5 py-3 last:border-0 dark:border-riot-navy-700"
              >
                <span className="text-sm text-gray-700 dark:text-riot-navy-200">{label}</span>
                <div className="text-center">
                  <input
                    type="checkbox"
                    checked={pref.inApp}
                    onChange={() => toggle(key, "inApp")}
                    className="h-4 w-4 rounded border-gray-300 text-riot-gold-500"
                    aria-label={`${label} in-app notifications`}
                  />
                </div>
                <div className="text-center">
                  <input
                    type="checkbox"
                    checked={pref.email}
                    onChange={() => toggle(key, "email")}
                    className="h-4 w-4 rounded border-gray-300 text-riot-gold-500"
                    aria-label={`${label} email notifications`}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={!local || save.isPending}
        className="mt-4 rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {save.isPending ? "Saving…" : "Save Preferences"}
      </button>
      {save.isSuccess && (
        <p className="mt-2 text-xs text-green-600 dark:text-green-400">Preferences saved.</p>
      )}
      {save.isError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          Save failed: {String((save.error as Error).message ?? save.error)}
        </p>
      )}
    </div>
  );
}
